import { ApiResponseError } from 'app/types/api-status';
import i18next from 'i18next';

import { getRouterPathname } from './app-path';
import { notify } from './toast-notification';
import { clearSession } from './sessions';

/**
 * Parsing API response error.
 * @param error Server response error.
 */
export function parseApiResponseError(error: unknown) {
  const err = error as ApiResponseError;
  return err;
}

/**
 * Redirect unauthorized request or the session invalidate request like system sign out.
 */
export function redirectBase() {
  clearSession();
  window.location.href = import.meta.env.BASE_URL;
}

/**
 * Handling server error response.
 * @param error Server response status code.
 */
export function onHandleApiErrorResponse(error: unknown) {
  const err = parseApiResponseError(error);

  const notifyMessage = (() => {
    const msg = err.message as unknown;
    if (typeof msg === 'string') {
      return msg;
    }
    if (
      msg &&
      typeof msg === 'object' &&
      'message' in msg &&
      typeof (msg as { message: unknown }).message === 'string'
    ) {
      return (msg as { message: string }).message;
    }
    return err.statusText || 'An error occurred';
  })();

  if (err.status === 401) {
    const routerPathname = getRouterPathname();
    const isOnProtectedRoute =
      routerPathname === '/dashboard' ||
      routerPathname.startsWith('/dashboard/');
    if (isOnProtectedRoute) {
      // User was logged in and using the portal; session expired (e.g. inactivity).
      // Show toast so they understand why they are being sent to login.
      const toastId = notify.warn(i18next.t('Messages.sessionExpired'), {
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
      });
      setTimeout(() => {
        if (toastId) notify.dismiss(toastId);
        redirectBase();
      }, 2000);
    } else {
      // User on login/homepage or public page; no active session. Redirect without toast.
      redirectBase();
    }
  } else if ([500, 504].includes(err.status)) {
    console.log('Server error:', err);
    notify.error(notifyMessage);
    // Request cancellation
  } else if (err.message === 'canceled') {
    console.info('Request was canceled');
  } else {
    console.log('Other error:', err);

    notify.error(notifyMessage);
  }
}
